import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";

import {
  Disclose,
  EmptyState,
  Loading,
  Permission,
  Refusal,
  hostConfig,
  kernel,
} from "@lopie/kernel-ui";

/**
 * HR Employee Directory.
 *
 * Lists the Person records this app and this person may both see, lets HR
 * search them, open one, and correct its details. Columns and editable
 * fields come from GET /schema (the Person type), the collection path from
 * /schema, and the ceiling from GET /apps — nothing about the kernel's shape
 * is copied here.
 *
 * Edits go to the kernel as a person.update. A corrected value is held over
 * what a connector reports, so every field that carries a `held` entry says
 * so, at the field.
 */

const TYPE = "Person";
const PAGE = 200;
const MAX_PAGES = 25;

function toError(status, body) {
  const e = new Error((body && (body.message || body.error)) || `HTTP ${status}`);
  e.status = status;
  e.body = body;
  e.isManifest = body?.error === "not_in_manifest";
  return e;
}

function show(value) {
  if (value === undefined || value === null || value === "") return "";
  if (Array.isArray(value)) return value.filter((v) => v !== "" && v != null).join(", ");
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

function fieldsOf(item) {
  return item?.fields || item || {};
}

function heldNote(h) {
  if (!h) return "";
  if (typeof h !== "object") return String(h);
  const who = h.by || h.set_by || h.actor || "someone";
  const when = h.at || h.set_at || h.when || "";
  const via = h.app || h.via || "";
  return `Corrected by ${who}${when ? ` on ${String(when).slice(0, 10)}` : ""}${via ? ` through ${via}` : ""} — not what the source reported`;
}

export default function App({ token, claims, onSignOut }) {
  const [state, setState] = useState(null);
  const [failure, setFailure] = useState(null);
  const [query, setQuery] = useState("");
  const [openId, setOpenId] = useState(null);

  const get = useMemo(() => kernel(token), [token]);
  const api = hostConfig()?.api || "";

  const load = useCallback(async () => {
    try {
      const [apps, schema] = await Promise.all([get("/apps"), get("/schema")]);
      const self = (apps.apps || []).find((a) => a.is_self);
      const declared = (self?.reads || []).some((g) => g.type === TYPE);
      const actions = self?.actions || [];
      const canEdit = actions.some((a) => (typeof a === "string" ? a : a?.verb) === "person.update");
      const t = (schema.types || []).find((x) => x.type === TYPE);
      if (!t) {
        setState({ missing: true });
        return;
      }
      let items = [];
      let body = null;
      let refusal = null;
      let truncated = false;
      if (declared) {
        try {
          let cursor;
          for (let i = 0; i < MAX_PAGES; i += 1) {
            const params = { limit: PAGE };
            if (cursor) params.cursor = cursor;
            body = await get(`/${t.collection}`, params);
            items = items.concat(body.items || []);
            if (!body.has_more) break;
            cursor = body.cursor;
            if (i === MAX_PAGES - 1) truncated = true;
          }
        } catch (e) {
          refusal = e;
        }
      }
      setState({
        type: t,
        declared,
        canEdit,
        items,
        refusal,
        truncated,
        permitted: body?.permitted_tags || [],
        declaredTags: body?.declared_tags || null,
      });
      setFailure(null);
    } catch (e) {
      setFailure(e);
    }
  }, [get]);

  useEffect(() => {
    load();
  }, [load]);

  const save = useCallback(
    async (id, changes) => {
      const r = await fetch(`${api}/${state.type.collection}/${encodeURIComponent(id)}`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}`, "content-type": "application/json" },
        body: JSON.stringify({ fields: changes }),
      });
      const text = await r.text();
      let body = null;
      try {
        body = JSON.parse(text);
      } catch {
        body = text ? { message: text } : null;
      }
      if (!r.ok) throw toError(r.status, body);
      await load();
      return body;
    },
    [api, token, state, load],
  );

  if (failure) {
    return (
      <Box sx={{ p: 3 }}>
        <Refusal failure={failure} actor="HR Employee Directory" onRetry={load} />
      </Box>
    );
  }
  if (!state) return <Loading label="Reading employees from the kernel" minHeight="60vh" />;

  const label = state.type?.label || "display_name";
  const columns = (state.type?.fields || []).filter(
    (f) => ["display_name", "emails", "phones", "org_unit", "status"].includes(f.name),
  );
  const q = query.trim().toLowerCase();
  const shown = (state.items || []).filter((item) => {
    if (!q) return true;
    const f = fieldsOf(item);
    return Object.values(f).some((v) => show(v).toLowerCase().includes(q));
  });

  return (
    <Stack spacing={3} sx={{ p: { xs: 2, sm: 3 }, minWidth: 0 }}>
      <Stack
        direction="row"
        spacing={2}
        sx={{ alignItems: "baseline", justifyContent: "space-between", flexWrap: "wrap" }}
      >
        <Typography variant="h1">HR Employee Directory</Typography>
        <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
          <Typography variant="body2" color="text.secondary">
            {claims?.username || ""}
          </Typography>
          <Typography
            component="button"
            variant="body2"
            onClick={onSignOut}
            sx={{ background: "none", border: 0, cursor: "pointer", color: "primary.main" }}
          >
            Sign out
          </Typography>
        </Stack>
      </Stack>

      <Permission
        gate={
          "Shows the employee records this app's reviewed manifest allows, "
          + "narrowed further by your own groups."
        }
        groups={claims?.["cognito:groups"] || []}
        effective={{ permitted: state.permitted || [], declared: state.declaredTags }}
      />

      {state.missing ? (
        <Alert severity="warning" variant="outlined">
          <AlertTitle>The kernel describes no Person type</AlertTitle>
          This app is built around people records, and the kernel&apos;s schema does not list one right now.
        </Alert>
      ) : null}

      {!state.missing && !state.declared ? (
        <EmptyState title="This app does not declare Person records">
          <Typography variant="body2">
            Its approved manifest has no read for people, so it never asks for them. That is the
            app&apos;s ceiling, not something about you.
          </Typography>
        </EmptyState>
      ) : null}

      {state.refusal ? (
        <Refusal failure={state.refusal} actor="HR Employee Directory" onRetry={load} />
      ) : null}

      {state.declared && !state.refusal ? (
        <>
          <Box sx={{ borderLeft: 3, borderColor: "primary.main", pl: 2 }}>
            <Typography
              sx={{ fontSize: "3rem", fontWeight: 300, lineHeight: 1.1 }}
              title="People records returned to you by the kernel right now, through this app's ceiling and your own groups."
            >
              {state.items.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              people you can see here · now{state.truncated ? " · first pages only" : ""}
            </Typography>
          </Box>

          <TextField
            label="Search by name, email, phone or team"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            size="small"
            sx={{ maxWidth: 420 }}
          />

          {state.items.length === 0 ? (
            <EmptyState
              title="No employees you may see"
              tagged={(state.permitted || []).join(", ")}
            >
              <Typography variant="body2">
                The kernel answered and returned nobody for you. That is about your own access,
                not this app&apos;s.
              </Typography>
            </EmptyState>
          ) : shown.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Nobody matches “{query}”.
            </Typography>
          ) : (
            <Box sx={{ overflowX: "auto", minWidth: 0 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {columns.map((c) => (
                      <TableCell key={c.name} title={c.description || ""}>
                        {c.name.replace(/_/g, " ")}
                      </TableCell>
                    ))}
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {shown.map((item) => {
                    const f = fieldsOf(item);
                    const open = openId === item.id;
                    return [
                      <TableRow key={item.id} hover selected={open}>
                        {columns.map((c) => (
                          <TableCell key={c.name}>
                            {show(f[c.name]) || (
                              <Typography variant="caption" color="text.secondary">
                                not set
                              </Typography>
                            )}
                            {item.held?.[c.name] ? (
                              <Chip
                                size="small"
                                variant="outlined"
                                label="corrected"
                                title={heldNote(item.held[c.name])}
                                sx={{ ml: 1 }}
                              />
                            ) : null}
                          </TableCell>
                        ))}
                        <TableCell>
                          <Button size="small" onClick={() => setOpenId(open ? null : item.id)}>
                            {open ? "Close" : state.canEdit ? "Open / edit" : "Open"}
                          </Button>
                        </TableCell>
                      </TableRow>,
                      open ? (
                        <TableRow key={`${item.id}-detail`}>
                          <TableCell colSpan={columns.length + 1}>
                            <Detail
                              item={item}
                              type={state.type}
                              label={label}
                              canEdit={state.canEdit}
                              onSave={save}
                            />
                          </TableCell>
                        </TableRow>
                      ) : null,
                    ];
                  })}
                </TableBody>
              </Table>
            </Box>
          )}

          <Disclose label="What this list is and is not">
            <Stack spacing={1} sx={{ maxWidth: "62ch" }}>
              <Typography variant="body2" color="text.secondary">
                These are the Person records the kernel returned to you through this app. Which
                ones that is depends on this app&apos;s approved ceiling and on your own groups;
                someone with other groups may see a different list.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Job title, start date, manager and pay are not fields the kernel holds, so they
                are not shown here.
              </Typography>
            </Stack>
          </Disclose>
        </>
      ) : null}
    </Stack>
  );
}

function Detail({ item, type, label, canEdit, onSave }) {
  const f = fieldsOf(item);
  const editable = (type.fields || []).filter((x) => x.type === "string");
  const initial = useMemo(
    () =>
      Object.fromEntries(
        editable.map((x) => [x.name, x.repeated ? (f[x.name] || []).join(", ") : f[x.name] ?? ""]),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [item],
  );
  const [draft, setDraft] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(null);

  const changes = {};
  for (const x of editable) {
    if (draft[x.name] === initial[x.name]) continue;
    changes[x.name] = x.repeated
      ? String(draft[x.name] || "")
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean)
      : draft[x.name];
  }
  const dirty = Object.keys(changes).length > 0;
  const missingRequired = editable.some((x) => x.required && !String(draft[x.name] || "").trim());

  const submit = async () => {
    setBusy(true);
    setError(null);
    setDone(null);
    try {
      await onSave(item.id, changes);
      setDone(Object.keys(changes));
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Typography variant="h6">{show(f[label]) || item.id}</Typography>
        <Box
          sx={{
            display: "grid",
            gap: 2,
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          }}
        >
          {editable.map((x) => (
            <Box key={x.name}>
              <TextField
                fullWidth
                size="small"
                label={x.name.replace(/_/g, " ") + (x.required ? " (required)" : "")}
                value={draft[x.name] ?? ""}
                onChange={(e) => setDraft({ ...draft, [x.name]: e.target.value })}
                disabled={!canEdit || busy}
                helperText={
                  [x.repeated ? "Separate several with commas." : "", x.description || ""]
                    .filter(Boolean)
                    .join(" ")
                }
              />
              {item.held?.[x.name] ? (
                <Typography variant="caption" sx={{ display: "block", color: "kernel.attention" }}>
                  {heldNote(item.held[x.name])}
                </Typography>
              ) : null}
            </Box>
          ))}
        </Box>

        {!canEdit ? (
          <Typography variant="body2" color="text.secondary">
            Read only: this app&apos;s approved manifest does not include updating people.
          </Typography>
        ) : (
          <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
            <Button variant="contained" disabled={!dirty || busy || missingRequired} onClick={submit}>
              {busy ? "Saving…" : "Save changes"}
            </Button>
            <Button disabled={!dirty || busy} onClick={() => setDraft(initial)}>
              Undo
            </Button>
            {missingRequired ? (
              <Typography variant="caption" color="text.secondary">
                A required field is empty.
              </Typography>
            ) : null}
          </Stack>
        )}

        {error ? <Refusal failure={error} actor="HR Employee Directory" /> : null}
        {done ? (
          <Alert severity="info" variant="outlined">
            <AlertTitle>Saved: {done.map((d) => d.replace(/_/g, " ")).join(", ")}</AlertTitle>
            The kernel accepted the change and records you as the person who made it. It now
            stands over what a connected source reports for those fields; it does not change the
            record in that source itself.
          </Alert>
        ) : null}
      </Stack>
    </Paper>
  );
}
